
import Vue from 'vue';
import isMobileView from '@root/common/utils/isMobileView';
import { Category } from '@root/common/types/fragment';
import ContentImage from '@core/components/UI/ContentImage.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import getItemFromObject from 'lodash/get';
import { CustomCrop } from '@root/common/types/fragment';

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

interface Props {
  category: Category;
}

interface Data {
  isMobileView: boolean;
  href: string;
}

interface Methods {
  getItemFromObject: typeof getItemFromObject;
  getCropperData(index?: number): Record<string, unknown> | null;
  getCustomCrop(ratio: string, index?: number): CustomCrop | undefined;
  getFocuspoint(index?: number): Record<string, unknown>;
}

const pictureSrcset: PictureSrcset = { xs: { w: 120, h: 120, r: '1:1' }, sm: { w: 282, h: 282, r: '1:1' }, lg: { w: 450, h: 450, r: '1:1' } };
const sizeList: ['lg', 'sm', 'xs'] = ['lg', 'sm', 'xs'];

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    ContentImage,
    LinkHandler,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isMobileView: false,
      href: '',
    };
  },
  methods: {
    getItemFromObject,
    getCustomCrop(ratio) {
      const image = this.category.attributes?.image;
      if (!image) {
        return undefined;
      }

      const customCrops = image!.attributes?.cropperData?.customCrops || [];

      return customCrops.find((crop: { ratio: string }) => crop.ratio === ratio);
    },
    getFocuspoint() {
      const cropperProps: Record<string, unknown> = {};

      const image = this.category.attributes?.image;

      const focuspoint = image?.attributes?.cropperData?.focuspoint;
      if (focuspoint?.x !== undefined && focuspoint?.x !== null) {
        cropperProps.fx = focuspoint.x;
      } else {
        cropperProps.fx = '0.5';
      }

      if (focuspoint?.y !== undefined && focuspoint?.y !== null) {
        cropperProps.fy = focuspoint.y;
      } else {
        cropperProps.fy = '0.5';
      }

      return cropperProps;
    },
    getCropperData() {
      if (!this.category) {
        return null;
      }

      const cropperData: Record<string, unknown> = {};

      for (const size of sizeList) {
        if (pictureSrcset?.[size]) {
          const sizeSrcset = pictureSrcset[size];

          const customCrop = this.getCustomCrop(sizeSrcset?.r || '1:1');

          if (customCrop) {
            cropperData[size] = { ...sizeSrcset, customCrop };
          } else {
            cropperData[size] = sizeSrcset;
          }
        }
      }

      const image = this.category.attributes?.image;
      if (!image) {
        return null;
      }

      return {
        ...cropperData,
        ...(image!.attributes?.cropperData || {}),
        base: this.getFocuspoint(),
      };
    },
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
});
